/**
 * CloudGMCustomerArchive — iter-259 (Feb 2026)
 * ============================================
 * Archive hub for the CloudGM CUSTOMER platform. Mirrors
 * CloudGMBusinessArchive with a single card at launch:
 *   • الحسابات المؤرشفة (Archived Customer Accounts).
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Users } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';
import { usePerms } from '../../permissions/PermissionsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CloudGMCustomerArchive() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const [count, setCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/cgc-users/archived`,
                                 { headers: getAuthHeaders() });
      setCount(r.data?.count || 0);
    } catch (_e) { /* fail-soft */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const CARDS = [
    {
      id: 'accounts',
      nameAr: 'الحسابات المؤرشفة',
      nameEn: 'Archived Accounts',
      descAr: 'استعد الحسابات المؤرشفة أو احذفها نهائياً',
      descEn: 'Restore archived customer accounts or delete them permanently',
      icon: Users,
      color: '#1E9BD9',
      route: '/control/archive/cloudgm/accounts',
      count,
    },
  ];

  return (
    <DashboardLayout
      title="أرشيف CLOUDGM"
      titleEn="CLOUDGM Archive"
      subtitle="اختر فئة الأرشيف التي تريد إدارتها"
      subtitleEn="Pick the archive category you want to manage"
      screenCode="ARC-CGM"
      backPath="/control/archive"
      onBack={() => navigate('/control/archive')}
      breadcrumb={[
        { labelAr: 'الأرشيف', labelEn: 'Archive', path: '/control/archive' },
        { labelAr: 'CLOUDGM', labelEn: 'CLOUDGM' },
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="cloudgm-customer-archive">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {visiblePages(CARDS, 'route').map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.button
                key={card.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ y: -5 }}
                onClick={() => navigate(card.route)}
                className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-start transition-all hover:border-[#1E9BD9]/40"
                data-testid={`archive-card-cgc-${card.id}`}
              >
                <div
                  className="w-14 h-14 rounded-xl mb-3 flex items-center justify-center border"
                  style={{ backgroundColor: `${card.color}15`, borderColor: `${card.color}40` }}
                >
                  <Icon className="h-7 w-7" style={{ color: card.color }} />
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-bold text-white">
                    {language === 'ar' ? card.nameAr : card.nameEn}
                  </h3>
                  {card.count > 0 && (
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-bold"
                      style={{ backgroundColor: `${card.color}25`, color: card.color }}
                    >
                      {card.count}
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#71717A] leading-relaxed">
                  {language === 'ar' ? card.descAr : card.descEn}
                </p>
              </motion.button>
            );
          })}
        </div>
      </section>
    </DashboardLayout>
  );
}
